import {useState} from "react";
import {Alert, Table, Tag} from "antd";
import type {ColumnsType, TablePaginationConfig} from "antd/es/table";
import {StyledDescriptionText} from "../../../../../../calls/upcoming-calls/upcoming-calls.style";
import {usePayoutHistory} from "../../../../../../../features/payouts";
import {PayoutHistoryEmptyState} from "../empty-state";

interface DataType {
  key: string;
  date: string;
  amount: string;
  fee: string;
  status: string;
  reference: string;
  transactionType: "DEBIT" | "CREDIT";
}

const columns: ColumnsType<DataType> = [
  {
    title: "Date",
    dataIndex: "date",
    fixed: "left",
    width: "17.425rem",
    key: "date",
    render: (text) => (
      <>
        <StyledDescriptionText>{text}</StyledDescriptionText>
      </>
    ),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    width: "17.425rem",
    key: "amount",
    render: (text) => (
      <>
        <StyledDescriptionText>{text}</StyledDescriptionText>
      </>
    ),
  },
  {
    title: "Fee",
    dataIndex: "fee",
    width: "17.425rem",
    key: "fee",
    render: (text) => (
      <>
        <StyledDescriptionText>{text}</StyledDescriptionText>
      </>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    width: "17.425rem",
    key: "status",
    render: (text) => (
      <>
        <Tag
          color={text === "In Progress" ? "yellow" : text === "Failed" ? "red" : "green"}
        >
          {text}
        </Tag>
      </>
    ),
  },
  {
    title: "Reference no.",
    dataIndex: "reference",
    width: "17.425rem",
    key: "reference",
  },
  {
    title: "Transaction Type",
    dataIndex: "transactionType",
    width: "17.425rem",
    key: "transactionType",
    render: (text) => (
      <>
        <StyledDescriptionText>{text}</StyledDescriptionText>
      </>
    ),
  },
];

const DEFAULT_PAGE_SIZE = 15;

export function PayoutHistoryTable() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const {data, status, error, isFetching} = usePayoutHistory({page, perPage: pageSize});

  function handlePayoutData(): DataType[] {
    if (status === "success" && data) {
      return data.data.map((item) => ({
        key: item.id.toString(),
        date: new Date(item.createdAt).toLocaleDateString(),
        amount: `${item.currency}${item.amount.toLocaleString("en-US")}`,
        fee: `${item.currency}${item.fee.toLocaleString("en-US")}`,
        status: item.statusName,
        reference: item.reference,
        transactionType: item.transactionType,
      }));
    }
    return [];
  }

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (pagination.current) {
      setPage(pagination.current);
    }
    if (pagination.pageSize) {
      setPageSize(pagination.pageSize);
    }
  };

  if (status === "error") {
    return <Alert message={error?.message || "An error occurred"} type="error" />;
  }

  if (status === "success" && data?.total === 0) {
    return <PayoutHistoryEmptyState />;
  }

  return (
    <Table
      columns={columns}
      dataSource={handlePayoutData()}
      scroll={{x: 100}}
      loading={status === "loading" || isFetching}
      onChange={handleTableChange}
      pagination={{
        current: page,
        pageSize: pageSize,
        total: data?.total ?? 0,
        showSizeChanger: true,
        pageSizeOptions: ["10", "15", "25", "50"],
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      }}
    />
  );
}
